import { useState } from "react";
import StepNav, { ADMISSION_STEPS } from "@/components/StepNav";
import { ADMISSION_CASES, serveCase } from "@/data/admission";
import { useCaseList } from "@/context/ContentContext";
import { AdmissionIntro, AdmissionDebrief } from "@/components/admission/AdmissionParts";
import {
  IdentityBeat,
  ConsentBeat,
  PreopBeat,
  HandoverBeat,
} from "@/components/admission/AdmissionBeats";
import { HousingBeat } from "@/components/admission/AdmissionInteractive";

const pickRandom = (cases, excludeId) => {
  const pool = cases.filter((c) => c.id !== excludeId);
  const list = pool.length ? pool : cases;
  return list[Math.floor(Math.random() * list.length)];
};

export default function Admission() {
  const cases = useCaseList("admission", ADMISSION_CASES);
  const [stage, setStage] = useState("intro"); // intro | running | debrief
  const [caseData, setCaseData] = useState(null);
  const [beatIdx, setBeatIdx] = useState(0);
  const [scores, setScores] = useState([]);
  const [debrief, setDebrief] = useState(null);

  const beginCase = (excludeId) => {
    setCaseData(serveCase(pickRandom(cases, excludeId)));
    setBeatIdx(0);
    setScores([]);
    setDebrief(null);
    setStage("running");
  };

  const handleBeatDone = (scoreObj) => {
    const next = [...scores, scoreObj];
    if (beatIdx < 4) {
      setScores(next);
      setBeatIdx(beatIdx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const earned = next.reduce((a, b) => a + b.earned, 0);
    const max = next.reduce((a, b) => a + b.max, 0) || 1;
    setDebrief({
      percent: Math.round((earned / max) * 100),
      breakdown: next,
      caseTitle: caseData.name,
    });
    setStage("debrief");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentStep = stage === "running" ? beatIdx + 1 : stage === "debrief" ? 5 : 1;
  const beatProps = { c: caseData, onDone: handleBeatDone };
  const beats = [
    <IdentityBeat key="identity" {...beatProps} />,
    <ConsentBeat key="consent" {...beatProps} />,
    <PreopBeat key="preop" {...beatProps} />,
    <HousingBeat key="house" {...beatProps} />,
    <HandoverBeat key="handover" {...beatProps} />,
  ];

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav
        current={currentStep}
        steps={ADMISSION_STEPS}
        title="Admissions"
        subtitle="Cert IV patient admission trainer"
      />

      <main className="max-w-3xl mx-auto px-6 py-10">
        {stage === "intro" && (
          <AdmissionIntro caseCount={cases.length} onStart={() => beginCase(null)} />
        )}

        {stage === "running" && caseData && (
          <div data-testid="admission-running">
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#0047AB] font-semibold">
                  Admitting · {caseData.name}
                </div>
                <div className="text-sm text-slate-600">
                  {caseData.breed} · {caseData.signalment}
                </div>
              </div>
              <div className="flex gap-1.5">
                {ADMISSION_STEPS.map((s) => (
                  <span
                    key={s.id}
                    className={`w-6 h-1.5 rounded-full ${s.id <= currentStep ? "bg-[#0047AB]" : "bg-slate-200"}`}
                  />
                ))}
              </div>
            </div>
            {beats[beatIdx]}
          </div>
        )}

        {stage === "debrief" && debrief && (
          <AdmissionDebrief
            result={debrief}
            onNext={() => beginCase(caseData.id)}
            onRestart={() => setStage("intro")}
          />
        )}
      </main>
    </div>
  );
}
